
export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

export interface FAQCategory {
  id: string;
  name: string;
  description: string;
}

export const faqCategories: FAQCategory[] = [
  {
    id: "getting-started",
    name: "Getting Started",
    description: "Basic information about using our platform"
  },
  {
    id: "image-generation",
    name: "Image Generation",
    description: "How to create and enhance images"
  },
  {
    id: "plans-billing",
    name: "Plans & Billing",
    description: "Subscription plans and payment information"
  },
  {
    id: "technical",
    name: "Technical",
    description: "Technical specifications and requirements"
  },
  {
    id: "account-support",
    name: "Account & Support",
    description: "Account management and customer support"
  },
  {
    id: "enterprise",
    name: "Enterprise",
    description: "Business and enterprise solutions"
  }
];

export const faqItems: FAQItem[] = [
  // Getting Started
  {
    id: "what-is-bulk-image-generator",
    question: "What is Bulk Image Generator?",
    answer: "Bulk Image Generator is an AI-powered platform that allows you to create multiple high-quality images at once using advanced AI models. You can generate images for various purposes including marketing, social media, e-commerce, and creative projects.",
    category: "getting-started"
  },
  {
    id: "how-to-get-started",
    question: "How do I get started with image generation?",
    answer: "Simply sign up for an account, choose your preferred AI model, write a descriptive prompt, and click generate. You can start with our free plan to explore the platform's capabilities.",
    category: "getting-started"
  },
  {
    id: "available-ai-models",
    question: "What AI models are available?",
    answer: "We support various state-of-the-art AI models including FLUX, Stable Diffusion, and other cutting-edge image generation models. Each model has its own strengths and is suitable for different types of images.",
    category: "getting-started"
  },
  {
    id: "tutorials-available",
    question: "Are there tutorials available to learn the platform?",
    answer: "Yes, we offer comprehensive video tutorials, written guides, and best practice documentation in our help center to help you master image generation techniques.",
    category: "getting-started"
  },

  // Image Generation
  {
    id: "effective-prompts",
    question: "How do I write effective prompts?",
    answer: "Be specific and descriptive in your prompts. Include details about style, colors, composition, lighting, and mood. For example, instead of 'a dog', try 'a golden retriever sitting in a sunny garden, photorealistic style, warm lighting'.",
    category: "image-generation"
  },
  {
    id: "prompt-enhancement",
    question: "How do I enhance my prompts for better results?",
    answer: "Use our built-in prompt enhancement feature that automatically improves your prompts with additional descriptive elements, style keywords, and technical parameters to achieve better results.",
    category: "image-generation"
  },
  {
    id: "multiple-variations",
    question: "Can I generate multiple images with different variations?",
    answer: "Yes! You can generate multiple variations of the same prompt to explore different creative possibilities. Each generation will produce unique results based on your prompt.",
    category: "image-generation"
  },
  {
    id: "generation-time",
    question: "How long does it take to generate images?",
    answer: "Generation time varies based on the selected model and current server load. Most images are generated within 30 seconds to 2 minutes. You'll receive real-time updates on the generation progress.",
    category: "image-generation"
  },
  {
    id: "generation-failure",
    question: "What happens if my image generation fails?",
    answer: "If a generation fails, you won't be charged for that attempt. Common reasons for failure include inappropriate content detection or technical issues. You can try rephrasing your prompt or contact support for assistance.",
    category: "image-generation"
  },
  {
    id: "manage-generated-images",
    question: "How do I manage my generated images?",
    answer: "All your generated images are stored in your personal gallery where you can view, download, organize, and manage them. You can also share images with others using our sharing feature.",
    category: "image-generation"
  },

  // Technical
  {
    id: "image-sizes-formats",
    question: "What image sizes and formats are supported?",
    answer: "We support various image sizes including square (1:1), landscape (16:9), and portrait (9:16) formats. Common resolutions include 512x512, 1024x1024, and other standard dimensions depending on the selected model.",
    category: "technical"
  },
  {
    id: "download-formats",
    question: "What file formats can I download?",
    answer: "Generated images are available in high-quality PNG format. You can download individual images or bulk download multiple images at once for convenience.",
    category: "technical"
  },
  {
    id: "edit-generated-images",
    question: "Can I edit or modify generated images?",
    answer: "While our platform focuses on image generation, you can download your images and edit them using external image editing software. We may introduce built-in editing features in future updates.",
    category: "technical"
  },
  {
    id: "api-access",
    question: "Do you offer API access for developers?",
    answer: "Yes, we provide API access for developers who want to integrate our image generation capabilities into their own applications. API documentation and pricing details are available for enterprise customers.",
    category: "technical"
  },
  {
    id: "data-security",
    question: "Is my data and generated content secure?",
    answer: "We take security seriously and use industry-standard encryption to protect your data. Your generated images and personal information are stored securely and are only accessible to you.",
    category: "technical"
  },

  // Plans & Billing
  {
    id: "generation-limits",
    question: "Is there a limit to how many images I can generate?",
    answer: "Generation limits depend on your subscription plan. Free users have daily limits, while paid plans offer higher limits or unlimited generation. Check your current plan details in your dashboard.",
    category: "plans-billing"
  },
  {
    id: "upgrade-subscription",
    question: "How do I upgrade or change my subscription plan?",
    answer: "You can upgrade your plan anytime from your account settings. Changes take effect immediately, and you'll have access to the new plan's features and limits right away.",
    category: "plans-billing"
  },
  {
    id: "cancel-subscription",
    question: "Can I cancel my subscription anytime?",
    answer: "Yes, you can cancel your subscription at any time from your account settings. Your plan will remain active until the end of your current billing period.",
    category: "plans-billing"
  },
  {
    id: "payment-methods",
    question: "What payment methods do you accept?",
    answer: "We accept major credit cards, debit cards, and various online payment methods. All payments are processed securely through encrypted payment gateways.",
    category: "plans-billing"
  },
  {
    id: "refunds",
    question: "Do you offer refunds?",
    answer: "Refund policies vary by plan and circumstances. Please refer to our terms of service or contact customer support to discuss specific refund requests.",
    category: "plans-billing"
  },
  {
    id: "commercial-use",
    question: "Can I use generated images commercially?",
    answer: "Yes, images generated through our platform can be used for commercial purposes according to our terms of service. You retain full rights to use the images you create for your business needs.",
    category: "plans-billing"
  },

  // Account & Support
  {
    id: "contact-support",
    question: "How do I contact customer support?",
    answer: "You can reach our support team through the contact form on our website, email support, or through the help center. We typically respond within 24 hours during business days.",
    category: "account-support"
  },
  {
    id: "report-issues",
    question: "How do I report inappropriate content or bugs?",
    answer: "If you encounter inappropriate content or technical issues, please use our reporting feature or contact support immediately. We take all reports seriously and investigate promptly.",
    category: "account-support"
  },
  {
    id: "stay-updated",
    question: "How do I stay updated on new features?",
    answer: "Follow our blog, subscribe to our newsletter, or check the announcements section in your dashboard to stay informed about new features, model updates, and platform improvements.",
    category: "account-support"
  },
  {
    id: "request-features",
    question: "Can I request specific AI models or features?",
    answer: "We value user feedback and consider feature requests for future updates. You can submit suggestions through our feedback form or contact support with your ideas.",
    category: "account-support"
  },
  {
    id: "content-policies",
    question: "What content policies apply to image generation?",
    answer: "We have strict content policies that prohibit generating inappropriate, harmful, or copyrighted content. Our AI automatically filters content to ensure compliance with legal and ethical standards.",
    category: "account-support"
  },

  // Enterprise
  {
    id: "enterprise-solutions",
    question: "Do you offer enterprise solutions?",
    answer: "Yes, we offer enterprise plans with custom features, higher limits, priority support, and API access. Contact our sales team to discuss your organization's specific needs.",
    category: "enterprise"
  },
  {
    id: "team-collaboration",
    question: "Can I collaborate with team members?",
    answer: "Team collaboration features are available in our business and enterprise plans, allowing you to share projects, manage permissions, and work together on image generation projects.",
    category: "enterprise"
  }
];
